import type { AutomationDefinition, Step, StepType } from './api';
import { idsIn, newStepId } from './api';

/**
 * Editing a step tree, as pure functions over a path.
 *
 * A path is the indexes to walk, with `then`/`else` between them: `[0, 'then', 1]` is the second
 * step inside the first step's "when it holds" branch. Keeping the whole thing pure means the
 * screen never mutates the definition it is rendering, and means these are testable without a
 * browser — which matters, because a builder that quietly drops a branch is the kind of bug
 * somebody discovers weeks later in a run log.
 */

export type Branch = 'then' | 'else';
export type Path = (number | Branch)[];

export function emptyStep(type: StepType, taken: Set<string>): Step {
  const step: Step = { id: newStepId(type, taken), type };

  if (type === 'ACTION') return { ...step, action: '', config: {} };
  if (type === 'CONDITION') {
    return { ...step, conditions: { match: 'ALL', conditions: [] }, then: [], else: [] };
  }
  if (type === 'WAIT') return { ...step, wait: { hours: 1 } };

  return step;
}

/** The list a path points into, and the index within it. Returns null for a path that cannot be. */
function locate(steps: Step[], path: Path): { list: Step[]; index: number } | null {
  if (path.length === 0) return null;

  let list = steps;

  for (let at = 0; at < path.length - 1; at += 1) {
    const index = path[at];
    const branch = path[at + 1];

    if (typeof index !== 'number' || typeof branch !== 'string') return null;

    const parent = list[index];
    if (!parent) return null;

    list = parent[branch] ?? [];
    at += 1;
  }

  const last = path.at(-1);

  return typeof last === 'number' ? { list, index: last } : null;
}

/** Rebuilds the tree with one list replaced, without touching anything else. */
function rewrite(steps: Step[], path: Path, change: (list: Step[]) => Step[]): Step[] {
  if (path.length <= 1) return change(steps);

  const index = path[0];
  const branch = path[1];

  if (typeof index !== 'number' || typeof branch !== 'string') return steps;

  return steps.map((step, at) =>
    at === index ? { ...step, [branch]: rewrite(step[branch] ?? [], path.slice(2), change) } : step,
  );
}

export function addStep(
  definition: AutomationDefinition,
  into: Path,
  type: StepType,
): AutomationDefinition {
  const step = emptyStep(type, idsIn(definition.steps));

  // `into` names a list, not a position: `[]` is the top level, `[0, 'then']` a branch.
  return { steps: rewrite(definition.steps, [...into, 0], (list) => [...list, step]) };
}

export function updateStep(
  definition: AutomationDefinition,
  path: Path,
  change: (step: Step) => Step,
): AutomationDefinition {
  const found = locate(definition.steps, path);
  if (!found) return definition;

  return {
    steps: rewrite(definition.steps, path, (list) =>
      list.map((step, at) => (at === path.at(-1) ? change(step) : step)),
    ),
  };
}

export function removeStep(definition: AutomationDefinition, path: Path): AutomationDefinition {
  const found = locate(definition.steps, path);
  if (!found) return definition;

  return {
    steps: rewrite(definition.steps, path, (list) => list.filter((_, at) => at !== path.at(-1))),
  };
}

/**
 * Moves a step one place up or down among its siblings.
 *
 * Only among siblings: dragging a step out of a branch and into another changes what it runs
 * after, which is a different decision from "this should happen first" and deserves its own
 * gesture rather than being something a mis-aimed drag can do.
 */
export function moveStep(
  definition: AutomationDefinition,
  path: Path,
  direction: -1 | 1,
): AutomationDefinition {
  const found = locate(definition.steps, path);
  if (!found) return definition;

  const from = path.at(-1) as number;
  const to = from + direction;

  if (to < 0 || to >= found.list.length) return definition;

  return {
    steps: rewrite(definition.steps, path, (list) => {
      const next = [...list];
      const [moved] = next.splice(from, 1);

      next.splice(to, 0, moved!);

      return next;
    }),
  };
}

/** How many steps the tree holds, branches included — what the header counts. */
export function countSteps(steps: Step[]): number {
  return steps.reduce(
    (total, step) => total + 1 + countSteps(step.then ?? []) + countSteps(step.else ?? []),
    0,
  );
}

/**
 * What is wrong with the definition, before the server is asked.
 *
 * The server validates too and is the authority; this exists so somebody sees "this action has no
 * body" beside the field rather than as a 422 after pressing save.
 */
export interface Problem {
  path: Path;
  stepId: string;
  code: 'no_action' | 'missing_config' | 'empty_branch' | 'no_conditions' | 'no_wait';
  detail?: string;
}

export function problemsIn(
  steps: Step[],
  requiredBy: (action: string) => readonly string[],
  path: Path = [],
): Problem[] {
  const problems: Problem[] = [];

  for (const [index, step] of steps.entries()) {
    const here: Path = [...path, index];

    if (step.type === 'ACTION') {
      if (!step.action) {
        problems.push({ path: here, stepId: step.id, code: 'no_action' });
      } else {
        for (const key of requiredBy(step.action)) {
          const value = step.config?.[key];

          if (value === undefined || value === null || String(value).trim() === '') {
            problems.push({ path: here, stepId: step.id, code: 'missing_config', detail: key });
          }
        }
      }
    }

    if (step.type === 'CONDITION') {
      if ((step.conditions?.conditions.length ?? 0) === 0) {
        problems.push({ path: here, stepId: step.id, code: 'no_conditions' });
      }

      if ((step.then?.length ?? 0) === 0 && (step.else?.length ?? 0) === 0) {
        problems.push({ path: here, stepId: step.id, code: 'empty_branch' });
      }

      problems.push(...problemsIn(step.then ?? [], requiredBy, [...here, 'then']));
      problems.push(...problemsIn(step.else ?? [], requiredBy, [...here, 'else']));
    }

    if (step.type === 'WAIT') {
      const wait = step.wait ?? {};
      const hasDelay = [wait.minutes, wait.hours, wait.days].some(
        (value) => typeof value === 'number' && value > 0,
      );

      if (!hasDelay && !wait.untilBusinessHours) {
        problems.push({ path: here, stepId: step.id, code: 'no_wait' });
      }
    }
  }

  return problems;
}
